import { required, minLength, email, helpers } from 'vuelidate/lib/validators'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { parse, compareAsc } from 'date-fns'
import portugueseCountries from 'i18n-iso-countries/langs/pt.json'
import englishCountries from 'i18n-iso-countries/langs/en.json'
import { isLessThanToday, isDateLessThan } from '~/validations/validations.js'

import Form from '~/utils/mixins/form'

const phoneRule = helpers.withParams({ async: true }, (value, countryCode) => {
  return new Promise((resolve) => {
    const phoneNumber = countryCode + value // full phone number = countryCode+Value
    const parsedNumber = parsePhoneNumberFromString(phoneNumber) // now we can validate the full phone number

    resolve(parsedNumber ? parsedNumber.isValid() : false)
  })
})

const isAtLeastNextDayRule = helpers.withParams({ async: true }, (value) => {
  return new Promise((resolve) => {
    if (!value) {
      resolve(false)
    }
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date())

    const today = new Date()
    const nextDay = new Date(today)
    nextDay.setDate(today.getDate() + 1)
    if (!isNaN(parsedDate)) {
      if (compareAsc(parsedDate, nextDay) === 1) {
        resolve(true)
      } else {
        resolve(false)
      }
    } else {
      resolve(false)
    }
  })
})

const isSecondDateGreaterThanFirstRule = helpers.withParams(
  { async: true },
  (firstDate, secondDate) => {
    return new Promise((resolve) => {
      const firstDateParsed = parse(firstDate, 'dd/MM/yyyy', new Date())

      const secondDateParsed = parse(secondDate, 'dd/MM/yyyy', new Date())

      if (!isNaN(firstDateParsed) || !isNaN(secondDateParsed)) {
        if (compareAsc(secondDateParsed, firstDateParsed) === 1) {
          resolve(true)
        } else {
          resolve(false)
        }
      } else {
        resolve(false)
      }
    })
  }
)
const sameAs = (value) => value === true
export default {
  mixins: [Form],
  components: {
    InputBlock: () => import('~/components/commom/input-block/index'),
    ErrorBlock: () => import('~/components/commom/error-block/index'),
    DateBlock: () => import('~/components/commom/date-block/index'),
    RadioBlock: () => import('~/components/commom/radio-block/index'),
    ButtonBlock: () => import('~/components/commom/button-block/index'),
    CheckboxBlock: () => import('~/components/commom/checkbox-block/index'),
    SelectBlock: () => import('~/components/commom/select-block/index')
  },
  data() {
    return {
      fullName: '',
      email: '',
      phone: '',
      applicantDateOfBirth: null,
      secondApplicantDateOfBirth: null,
      countryCode: '+351',
      duration: null,
      start_date: null,
      end_date: null,
      isCountryCode: true,
      privacyPolicy: false,
      termsAndConditions: false,
      country: null,
      numberOfApplicants: 1,
      options: {
        numberOfApplicants: [
          {
            value: 1,
            name: this.$i18n.t('One')
          },
          {
            value: 2,
            name: this.$i18n.t('Two')
          },
          {
            value: 3,
            name: this.$i18n.t('Three')
          },
          {
            value: 4,
            name: this.$i18n.t('Four')
          },
          {
            value: 5,
            name: this.$i18n.t('Five')
          }
        ],
        portugueseCountriesArray: Object.keys(
          portugueseCountries.countries
        ).map((key) => {
          const country = portugueseCountries.countries[key]

          return {
            value: key,
            name: Array.isArray(country) ? country[0] : country
          }
        }),
        englishCountriesArray: Object.keys(englishCountries.countries).map(
          (key) => {
            const country = englishCountries.countries[key]

            return {
              value: key,
              name: Array.isArray(country) ? country[0] : country
            }
          }
        ),
        countryCode: [
          {
            value: '+244',
            name: 'AO (+244)' // Angola
          },
          {
            value: '+43',
            name: 'AT (+43)' // Austria
          },
          {
            value: '+32',
            name: 'BE (+32)' // Belgium
          },
          {
            value: '+55',
            name: 'BR (+55)' // Brazil
          },
          {
            value: '+359',
            name: 'BG (+359)' // Bulgaria
          },
          {
            value: '+238',
            name: 'CV (+238)' // Cape Verde
          },
          {
            value: '+385',
            name: 'HR (+385)' // Croatia
          },
          {
            value: '+357',
            name: 'CY (+357)' // Cyprus
          },
          {
            value: '+420',
            name: 'CZ (+420)' // Czech Republic
          },
          {
            value: '+45',
            name: 'DK (+45)' // Denmark
          },
          {
            value: '+372',
            name: 'EE (+372)' // Estonia
          },
          {
            value: '+358',
            name: 'FI (+358)' // Finland
          },
          {
            value: '+33',
            name: 'FR (+33)' // France
          },
          {
            value: '+49',
            name: 'DE (+49)' // Germany
          },
          {
            value: '+30',
            name: 'GR (+30)' // Greece
          },
          {
            value: '+245',
            name: 'GW (+245)' // Guinea-Bissau
          },
          {
            value: '+36',
            name: 'HU (+36)' // Hungary
          },
          {
            value: '+353',
            name: 'IE (+353)' // Ireland
          },
          {
            value: '+39',
            name: 'IT (+39)' // Italy
          },
          {
            value: '+371',
            name: 'LV (+371)' // Latvia
          },
          {
            value: '+423',
            name: 'LI (+423)' // Liechtenstein
          },
          {
            value: '+370',
            name: 'LT (+370)' // Lithuania
          },
          {
            value: '+352',
            name: 'LU (+352)' // Luxembourg
          },
          {
            value: '+356',
            name: 'MT (+356)' // Malta
          },
          {
            value: '+258',
            name: 'MZ (+258)' // Mozambique
          },
          {
            value: '+31',
            name: 'NL (+31)' // Netherlands
          },
          {
            value: '+48',
            name: 'PL (+48)' // Poland
          },
          {
            value: '+351',
            name: 'PT (+351)' // Portugal
          },
          {
            value: '+40',
            name: 'RO (+40)' // Romania
          },
          {
            value: '+239',
            name: 'ST (+239)' // São Tomé and Príncipe
          },
          {
            value: '+421',
            name: 'SK (+421)' // Slovakia
          },
          {
            value: '+386',
            name: 'SI (+386)' // Slovenia
          },
          {
            value: '+34',
            name: 'ES (+34)' // Spain
          },
          {
            value: '+46',
            name: 'SE (+46)' // Sweden
          },
          {
            value: '+41',
            name: 'CH (+41)' // Switzerland
          },
          {
            value: '+44',
            name: 'UK (+44)' // United Kingdom
          },
          {
            value: '+1',
            name: 'USA (+1)' // United States
          }
        ],
        duration: [
          {
            value: 'Até 1 Mês',
            name: this.$i18n.t('Up to 1 Month')
          },
          {
            value: 'De 1 a 2 Meses',
            name: this.$i18n.t('From 1 to 2 Months')
          },
          {
            value: 'De 2 a 3 Meses',
            name: this.$i18n.t('From 2 to 3 Months')
          },
          {
            value: 'De 3 a 6 Meses',
            name: this.$i18n.t('From 3 to 6 Months')
          },
          {
            value: 'De 6 a 9 Meses',
            name: this.$i18n.t('From 6 to 9 Months')
          },
          {
            value: 'anual',
            name: this.$i18n.t('Annual')
          }
        ]
      }
    }
  },

  computed: {
    firstName() {
      // Split the fullName into first name and last name
      return this.fullName.split(' ')[0] || ''
    },
    lastName() {
      // Split the fullName into first name and last name
      return (
        this.fullName
          .split(' ')
          .slice(1)
          .join(' ') || ''
      )
    },
    getCountryCode() {
      return this.countryCode
    },
    personalDetails() {
      return {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: this.countryCode + this.phone,
        applicantDateOfBirth: this.applicantDateOfBirth,
        start_date: this.start_date,
        country: this.country,
        end_date: this.end_date,
        numberOfApplicants: this.numberOfApplicants
      }
    }
  },

  validations() {
    const baseValidation = {
      fullName: {
        required,
        minLength: minLength(2),
        custom(value) {
          const names = value.split(' ')
          return names.length >= 2 && names.every((name) => name.length >= 2)
        }
      },
      email: {
        required,
        email
      },
      phone: {
        required,
        phoneRule: (value) => phoneRule(value, this.countryCode)
      },
      applicantDateOfBirth: {
        required,
        isLessThanToday,
        isDateLessThan: isDateLessThan(67)
      },
      privacyPolicy: {
        required,
        sameAs
      },
      termsAndConditions: {
        required,
        sameAs
      },
      country: {
        required
      },
      numberOfApplicants: {
        required
      },
      start_date: {
        required,
        isAtLeastNextDayRule: (value) => isAtLeastNextDayRule(value)
      },
      end_date: {
        required,
        isSecondDateGreaterThanFirstRule: (value) =>
          isSecondDateGreaterThanFirstRule(this.start_date, value)
      },
      countryCode: {
        required
      }
    }
    return baseValidation
  },

  methods: {
    getArrayOfCountries() {
      if (this.$i18n.locale === 'pt') {
        return this.options.portugueseCountriesArray
      } else {
        return this.options.englishCountriesArray
      }
    },
    focusNextInput(ref) {
      this.$nextTick(() => {
        this.$refs[ref].$el.querySelector('input').focus()
      })
    },
    validateForm() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.$nextTick(() => this.goToFirstError())
      }
    },
    goToNextStep() {
      this.validateForm()

      if (this.$v.$invalid) {
        return
      }
      this.$emit('input', this.personalDetails)
    },
    scrollTo(element) {
      const elementPosition =
        element.getBoundingClientRect().top + document.documentElement.scrollTop
      const halfOfViewport =
        Math.max(
          document.documentElement.clientHeight,
          window.innerHeight || 0
        ) / 2

      window.scrollTo(0, elementPosition - halfOfViewport)
    }
  }
}
